import {
  Actor,
  Color,
  Engine,
  Font,
  FontUnit,
  Text,
  vec,
  Vector,
} from "excalibur";
import { BuildError } from "../ecs/combinable";
import { TileComponent } from "../ecs/tile";
import { TowerAnimations } from "../resources";
import { CardKind, CardLevel, Playable } from "../types";
import { actorName } from "../utils/actor";
import { buildingName } from "../utils/building";

export class ErrorPreviewBuilding extends Actor {
  kind: CardKind;
  level: CardLevel;
  matchName: string;
  buildError: BuildError;
  buildChain: string[];
  tileX: number;
  mapCols: number;

  constructor({
    tilePos,
    playable: { kind, level },
    buildError,
    buildChain = [],
    mapCols,
  }: {
    tilePos: Vector;
    playable: Playable;
    buildError: BuildError;
    buildChain?: string[];
    mapCols: number;
  }) {
    const matchName = buildingName(kind, level);

    super({
      name: actorName(`${matchName}_error_preview`),
      width: 32,
      height: 32,
      z: 999, // Above regular buildings but below UI
      pos: tilePos,
    });

    this.kind = kind;
    this.level = level;
    this.matchName = matchName;
    this.buildError = buildError;
    this.buildChain = buildChain;
    this.tileX = tilePos.x;
    this.mapCols = mapCols;
  }

  override onInitialize(_engine: Engine) {
    // Set up graphics with red tint and semi-transparency
    const towerGraphic =
      TowerAnimations[
        this.matchName as keyof typeof TowerAnimations
      ].idle.clone();
    towerGraphic.opacity = 0.5;
    towerGraphic.tint = Color.Red;
    this.graphics.use(towerGraphic);

    // Center on tile
    const tileCenter = this.scene?.tileMaps[0].getTile(
      this.pos.x,
      this.pos.y,
    )?.center;
    if (tileCenter) {
      this.pos.setTo(tileCenter.x, tileCenter.y);
    }
    this.addComponent(new TileComponent());

    this.createMissingResourcesDisplay();
  }

  private createMissingResourcesDisplay() {
    const { missingUnlocks, missingResources } = this.buildError;

    let displayText = "";

    // Add build chain if it exists
    if (this.buildChain.length > 0) {
      displayText += `${this.buildChain.join(" → ")}\n\n`;
    }

    // Add missing unlocks
    if (missingUnlocks.length > 0) {
      displayText += `Needs: ${missingUnlocks.join(", ")}\n`;
    }

    // Add missing resources
    if (missingResources.length > 0) {
      const resourceLines = missingResources.map(
        ({ resource, required, available }) => {
          const shortage = required - available;
          return `${resource}: ${shortage} more`;
        },
      );
      displayText += resourceLines.join("\n");
    }

    if (displayText.trim()) {
      // Determine text position based on tile position
      // If tile is on the right side of the map, show text on the left
      const isRightSide = this.tileX > this.mapCols / 2;
      const xOffset = isRightSide ? -80 : 40;
      
      const textActor = new Actor({
        name: actorName(`${this.matchName}_error_text`),
        pos: vec(xOffset, 0),
        z: this.z + 1,
      });

      const font = new Font({
        family: "PixelSans",
        size: 12,
        unit: FontUnit.Px,
        color: Color.White,
        shadow: {
          offset: vec(1, 1),
          color: Color.Black,
          blur: 0.3,
        },
      });

      const textGraphic = new Text({
        text: displayText.trim(),
        font,
      });

      // Add semi-transparent background for better readability
      // Add extra padding for short text (single transitions)
      const extraPadding = this.buildChain.length <= 2 ? 6 : 0;
      const background = new Actor({
        name: actorName(`${this.matchName}_error_bg`),
        width: textGraphic.width + 12 + extraPadding,
        height: textGraphic.height + 6,
        color: new Color(0, 0, 0, 0.8),
        z: this.z,
      });
      background.pos = vec(xOffset, 0);
      this.addChild(background);

      textActor.graphics.use(textGraphic);
      this.addChild(textActor);
    }
  }
}
